import { useSuspenseQuery } from "@tanstack/react-query";
import { debounce } from "lodash-es";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router";

import type { TicketCategoryDto } from "@/api/types";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { ContextMenu } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { CalendarDateIcon } from "@/components/Icons/CalendarDateIcon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { GitBranch01Icon } from "@/components/Icons/GitBranch01Icon";
import { InfinityIcon } from "@/components/Icons/InfinityIcon";
import { SlashCircle01Icon } from "@/components/Icons/SlashCircle01Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { XCloseIcon } from "@/components/Icons/XCloseIcon";
import { TagList, transformConstraintToTag } from "@/components/Tag/TagList";
import { User } from "@/components/User/User";
import { createDashboardColumnHelper } from "@/design-system/Table/helpers";
import { TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { TableVisualIndicator } from "@/design-system/Table/TableVisualIndicator";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useSlug } from "@/hooks/useSlug";
import { useDeleteTicketCategory } from "@/queries/ticket-categories/mutations";
import { useTicketCategoryQueries } from "@/queries/ticket-categories/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { canManageCategories } from "../permissions";

export function TicketCategoryList(): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const { componentProps, openDeleteModal } = useDeleteModal<string>("rr-delete-modal-modal");
  const hasPermission = usePermission();
  const navigate = useNavigate();
  const [audienceHidden, audienceHiddenHandlers] = useBool();

  const ticketCategoriesQuery = useTicketCategoryQueries();
  const { data: ticketCategories } = useSuspenseQuery(ticketCategoriesQuery.list());
  const deleteTicketCategoryMutationResult = useDeleteTicketCategory();
  const hasTicketCategoryTree = ticketCategories.items.some((x) => x.type === "tree");
  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<TicketCategoryDto>();

    return [
      ...(hasTicketCategoryTree
        ? [
            helper.accessor("type", {
              header: "",
              cell: (cell) =>
                cell.getValue() === "tree" && (
                  <TableVisualIndicator>
                    <GitBranch01Icon />
                  </TableVisualIndicator>
                ),
              size: TABLE_COLUMN_SIZES.VISUAL_INDICATOR.DEFAULT,
              enableResizing: false,
              meta: {
                isVisualIndicator: true,
              },
            }),
          ]
        : []),
      helper.accessor("name", {
        header: t("model.repair-category.name"),
        cell: (cell) => cell.getValue(),
        meta: {
          shouldSimulateRowClick: true,
          renderTruncatedTooltip: (cell) => cell.getValue(),
        },
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("defaultAssignee", {
        header: t("model.repair-category.assigned"),
        cell: (cell) => {
          const value = cell.getValue();
          if (!value) {
            return null;
          }

          return <User user={value} size="sm" />;
        },
        minSize: TABLE_COLUMN_SIZES.USER.MIN,
        size: TABLE_COLUMN_SIZES.USER.DEFAULT,
      }),
      helper.accessor("audience", {
        header: t("model.repair-category.audience"),
        cell: (cell) => {
          const audience = cell.getValue();
          if (audience.length === 0) {
            return <i className="text-text-secondary">{t("model.constraint.placeholder")}</i>;
          }

          return <TagList tags={cell.getValue().map(transformConstraintToTag)} limit={3} />;
        },
        minSize: TABLE_COLUMN_SIZES.TAGS.MIN,
        size: TABLE_COLUMN_SIZES.TAGS.DEFAULT,
      }),
      helper.accessor("notificationType", {
        header: t("model.repair-category.notification-type"),
        cell: (cell) =>
          cell.row.original.type === "single" && (
            <span className="text-text-secondary">
              {cell.getValue() === "canBeCollective"
                ? t("model.repair-category.notification-type.collective")
                : t("model.repair-category.notification-type.private")}
            </span>
          ),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("reopenPolicy", {
        header: t("model.repair-category.reopen-policy"),
        cell: (cell) => {
          const value = cell.getValue();

          return (
            <>
              {value === "oneWithinFourteenDays" && <CalendarDateIcon />}
              {value === "unlimited" && <InfinityIcon />}
              {value === "never" && <SlashCircle01Icon />}
            </>
          );
        },
        meta: { cellAlignment: "center" },
        minSize: 64,
        size: 100,
      }),
      helper.accessor("disabled", {
        header: t("model.repair-category.status"),
        cell: (cell) => (cell.getValue() ? <XCloseIcon tone="danger-secondary" /> : <CheckIcon tone="success" />),
        meta: { cellAlignment: "center" },
        minSize: 64,
        size: 100,
      }),
      helper.accessor("id", {
        header: "",
        cell: (cell) => {
          const menuActions: ContextMenuItem[] = [
            {
              labels: {
                default: t("common.action.edit"),
              },
              value: "edit",
              icon: <Edit05Icon />,
              callbackFn: () => {
                void navigate(
                  routes.ticketCategories.edit({
                    slug,
                    id: cell.getValue(),
                  }),
                );
              },
              isHidden: !hasPermission(canManageCategories),
            },
            {
              labels: {
                default: t("common.action.delete"),
              },
              value: "delete",
              icon: <Trash02Icon />,
              callbackFn: () => openDeleteModal(cell.getValue()),
              isHidden: !cell.row.original.canDelete || !hasPermission(canManageCategories),
            },
          ];

          return <ContextMenu items={menuActions} />;
        },
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        enableResizing: false,
        meta: {
          isAction: true,
        },
      }),
    ];
  }, [hasTicketCategoryTree, t, hasPermission, slug, navigate, openDeleteModal]);
  const tableInstance = useResizableTable(TABLE_KEYS.TICKET_CATEGORIES, {
    columns,
    data: ticketCategories?.items ?? [],
    state: {
      columnVisibility: {
        audience: !audienceHidden,
      },
    },
    enableSorting: false,
  });
  const hideColumnsOnMobile = useMemo(
    () =>
      debounce(() => {
        const windowWidth = getWindowSize().width;
        if (windowWidth <= SM_SCREEN_SIZE && !audienceHidden) {
          audienceHiddenHandlers.setTrue();
        }
        if (windowWidth > SM_SCREEN_SIZE && audienceHidden) {
          audienceHiddenHandlers.setFalse();
        }
      }, 200),
    [audienceHidden, audienceHiddenHandlers],
  );
  useEffect(() => {
    hideColumnsOnMobile();
    window.addEventListener("resize", hideColumnsOnMobile);

    return () => {
      window.removeEventListener("resize", hideColumnsOnMobile);
    };
  }, [hideColumnsOnMobile]);

  return (
    <>
      <DeleteModal
        title={t("model.repair-category.action.delete.confirmation")}
        description={t("model.repair-category.action.delete.description")}
        onDelete={deleteTicketCategoryMutationResult.mutateAsync}
        deleteBtnProps={{
          "data-testid": "repair-categories-action-delete",
        }}
        {...componentProps}
      />
      <Table<TicketCategoryDto>
        table={tableInstance}
        data-testid="repair-categories-list"
        getRowHref={(row) =>
          routes.ticketCategories.edit({
            slug,
            id: row.original.id,
          })
        }
        readOnly={!hasPermission(canManageCategories)}
      />
    </>
  );
}
const SM_SCREEN_SIZE = 767;
function getWindowSize() {
  return {
    width: window.innerWidth,
  };
}
